import React, { useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import Calendar from '../Dashboard/widgets/Calendar';
import { useWingmanMetricsQuery } from '../../api/use-wingman-metrics-query';
import PerformanceTrendCard from './widgets/PerformanceTrendCard';
import SystemAlertsRow from './widgets/SystemAlertsRow';
import EventFunnelChart from './widgets/EventFunnelChart';
import ConversationFunnelChart from './widgets/ConversationFunnelChart';
import PoolHealthSection from './widgets/PoolHealthSection';
import ImpactAnalysisSection from './widgets/ImpactAnalysisSection';
import WingmanInsightsFooter from './widgets/WingmanInsightsFooter';
import {
  buildConversationFunnelStages,
  buildEventFunnelStages,
  buildImpactAnalysisItems,
  buildInsightsFooter,
  buildPerformanceTrendCards,
  buildPoolHealthRows,
  buildSystemAlerts,
  computeHealthSummary,
  EMPTY_PLACEHOLDER,
} from './wingmanIntelligenceMetrics';

const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 7);
  return [startDate, endDate];
};

const WingmanIntelligence = () => {
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [startDate, endDate] = dateRange;

  const { data: metrics = null, isLoading } = useWingmanMetricsQuery(startDate, endDate);

  const performanceCards = useMemo(() => buildPerformanceTrendCards(metrics), [metrics]);
  const conversationFunnelStages = useMemo(() => buildConversationFunnelStages(metrics), [metrics]);
  const eventFunnelStages = useMemo(() => buildEventFunnelStages(metrics), [metrics]);
  const poolHealthRows = useMemo(() => buildPoolHealthRows(metrics), [metrics]);
  const systemAlerts = useMemo(() => buildSystemAlerts(metrics), [metrics]);
  const impactItems = useMemo(() => buildImpactAnalysisItems(metrics), [metrics]);
  const insights = useMemo(() => buildInsightsFooter(metrics), [metrics]);
  const health = computeHealthSummary();
  const isHealthEmpty = health.label === EMPTY_PLACEHOLDER;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeOutlinedIcon sx={{ color: 'warning.main', fontSize: 28 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                color: (theme) => theme.palette.text.secondary,
              }}
            >
              Wingman Intelligence
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.875,
              px: 1.625,
              py: 0.5,
              borderRadius: '999px',
              bgcolor: 'background.paper',
              border: '1px solid #E0E0E0',
            }}
          >
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: isHealthEmpty ? '#9E9E9E' : '#43A047',
                flexShrink: 0,
              }}
            />
            <Typography
              component="span"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: isHealthEmpty ? '#5F6368' : '#2E7D32',
                letterSpacing: '-0.01em',
              }}
            >
              {health.label} ({health.detail})
            </Typography>
          </Box>
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>
          <Calendar startDate={startDate} endDate={endDate} onDateRangeChange={setDateRange} />
        </Box>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading Wingman metrics...
          </Typography>
        </Box>
      )}

      <SystemAlertsRow alerts={systemAlerts} />

      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: '#1B1B1F',
          letterSpacing: '-0.01em',
          mb: 1.5,
        }}
      >
        Performance Trends
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(5, 1fr)',
          },
          gap: '16px',
          mb: 3,
        }}
      >
        {performanceCards.map((card) => (
          <Box
            key={card.key}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              minHeight: 140,
            }}
          >
            <PerformanceTrendCard
              title={card.title}
              displayValue={card.displayValue}
              countLine={card.countLine}
            />
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '7fr 3fr' },
          gap: 2,
          mb: 2,
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            height: '100%',
            minHeight: 320,
            minWidth: 0,
          }}
        >
          <EventFunnelChart stages={eventFunnelStages} />
        </Box>
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            height: '100%',
            minHeight: 320,
            minWidth: 0,
          }}
        >
          <PoolHealthSection rows={poolHealthRows} />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '7fr 3fr' },
          gap: 2,
          mb: 2,
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            height: '100%',
            minHeight: 480,
            minWidth: 0,
          }}
        >
          <ConversationFunnelChart stages={conversationFunnelStages} />
        </Box>
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            height: '100%',
            minHeight: 380,
            minWidth: 0,
          }}
        >
          <ImpactAnalysisSection items={impactItems} />
        </Box>
      </Box>

      <WingmanInsightsFooter whatHappened={insights.whatHappened} nextSteps={insights.nextSteps} />
    </Box>
  );
};

export default WingmanIntelligence;
